import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Megaphone, X, ArrowRight, Bell } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UnreadAnnouncementPopup = ({ onNavigate }) => {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestAnnouncement, setLatestAnnouncement] = useState(null);

  useEffect(() => {
    if (!user) return;
    
    checkUnreadAnnouncements();
  }, [user]);

  const checkUnreadAnnouncements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/announcements`);
      const announcements = res.data.announcements || [];
      
      if (announcements.length === 0) return;
      
      // Ottieni data ultima lettura da localStorage
      const lastReadTime = localStorage.getItem('announcements_last_read');
      const lastReadDate = lastReadTime ? new Date(lastReadTime) : new Date(0);
      
      // Conta annunci non letti (pubblicati dopo l'ultima lettura)
      const unread = announcements.filter(a => new Date(a.created_at) > lastReadDate);
      
      if (unread.length > 0) {
        setUnreadCount(unread.length);
        setLatestAnnouncement(unread[0]); // Il più recente
        
        // Mostra popup dopo 2 secondi
        setTimeout(() => setShow(true), 2000);
      }
    } catch (error) {
      console.error('Error checking announcements:', error);
    }
  };

  const handleRead = () => {
    // Salva timestamp lettura
    localStorage.setItem('announcements_last_read', new Date().toISOString());
    setShow(false);
    
    // Naviga alla sezione comunicazioni
    if (onNavigate) {
      onNavigate('comunicazioni');
    }
  };

  const handleDismiss = () => {
    setShow(false);
    // Non segna come letto, apparirà di nuovo al prossimo accesso
  };

  if (!show || !latestAnnouncement) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-slate-900 border border-violet-500/50 rounded-2xl p-6 max-w-sm w-full shadow-2xl shadow-violet-500/20 animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Megaphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">Nuove Comunicazioni</h3>
              <p className="text-violet-400 text-sm">
                {unreadCount} {unreadCount === 1 ? 'messaggio' : 'messaggi'} da leggere
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Anteprima ultimo annuncio */}
        <div className="bg-slate-800/50 rounded-xl p-4 mb-4 border border-slate-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-violet-400" />
            <span className="text-xs text-violet-400 uppercase font-medium">Ultima comunicazione</span>
          </div>
          <h4 className="text-white font-semibold mb-1 line-clamp-1">
            {latestAnnouncement.title}
          </h4>
          <p className="text-slate-400 text-sm line-clamp-2">
            {latestAnnouncement.body}
          </p>
          {latestAnnouncement.attachments?.length > 0 && (
            <p className="text-cyan-400 text-xs mt-2">
              📎 {latestAnnouncement.attachments.length} allegat{latestAnnouncement.attachments.length > 1 ? 'i' : 'o'}
            </p>
          )}
        </div>

        {/* Azioni */}
        <div className="flex gap-3">
          <button
            onClick={handleRead}
            className="flex-1 py-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
          >
            Leggi ora
            <ArrowRight className="w-5 h-5" />
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-all"
          >
            Dopo
          </button>
        </div>

        {/* Nota */}
        <p className="text-center text-slate-500 text-xs mt-4">
          Resta aggiornato sulle novità della community!
        </p>
      </div>
    </div>
  );
};

export default UnreadAnnouncementPopup;
